import { FunctionComponent, useCallback, useMemo } from 'react';
import styles from '../styles.module.scss';
import { OccurrenceDateTimeLabel } from '../components/OccurrenceDateTimeLabel';
import { OccurrencePrice } from '../components/OccurrencePrice';
import DeliveryIndicator from '../../../DeliveryIndicator';
import { OccurrenceExtras } from '../components/OccurrenceExtras';
import { OccurrenceButton } from '../components/OccurrenceButton';
import { DateIcon } from '../../../DateIcon';
import {
    PublicBlendedCourse,
    PublicClassCourse,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import { Temporal } from 'temporal-polyfill';
import { isChildOccurrence } from '../../../../services/occurrences/isChildOccurrence';
import { VatSettings } from '@shared/types';
type OccurrenceCardProps = {
    course: PublicClassCourse | PublicBlendedCourse;
    hidePricing?: boolean;
    isSoldOut: boolean;
    isWaitlistEnabled: boolean;
    occurrence: PublicOccurrence;
    occurrenceCardClickHandler: (selectedOccurrence: PublicOccurrence) => void;
    vatSettings?: VatSettings;
    blockOccurrence?: PublicOccurrence;
};

export const OccurrenceCard: FunctionComponent<OccurrenceCardProps> = ({
    course,
    hidePricing,
    isSoldOut,
    isWaitlistEnabled,
    occurrence,
    occurrenceCardClickHandler,
    vatSettings,
    blockOccurrence,
}) => {
    const startDate = useMemo(
        () => Temporal.ZonedDateTime.from(occurrence.times.start.zoned_datetime),
        [occurrence]
    );
    const isBlock: boolean = useMemo((): boolean => occurrence.is_block === true, [occurrence]);
    const isChild: boolean = useMemo((): boolean => isChildOccurrence(occurrence), [occurrence]);
    const isChildSoldOut: boolean = useMemo(() => {
        if (isChild) {
            return occurrence && occurrence.available_spaces === 0;
        }
        return false;
    }, [isChild, occurrence]);
    const isBookable = useMemo(() => {
        return !isChildOccurrence(occurrence) || occurrence.individually_bookable === true;
    }, [occurrence]);
    const occurrenceCardClassName = useMemo(() => {
        if (isSoldOut && !isWaitlistEnabled) {
            return styles.occurrenceCardDisabled;
        }
        if (isBookable) {
            return styles.bookableOccurrenceCard;
        }
        return styles.occurrenceCard;
    }, [isBookable, isSoldOut, isWaitlistEnabled]);
    const isApplication: boolean = useMemo(() => {
        return course.application_required === true;
    }, [course]);

    const handleOccurrenceCardClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            if (isBookable || isWaitlistEnabled) {
                occurrenceCardClickHandler(occurrence);
            }
        },
        [isBookable, isWaitlistEnabled, occurrence, occurrenceCardClickHandler]
    );

    return (
        <li
            className={occurrenceCardClassName}
            onClick={handleOccurrenceCardClick}
            data-testid="occurrence-card"
        >
            <div className={styles.occurrenceCardContent}>
                <DateIcon date={startDate.toString()} />
                <div className={styles.occurrenceDetails}>
                    <div>
                        {occurrence.name && <span>{occurrence.name}</span>}
                        <div className={styles.occurrenceDateTimeLabelContainer}>
                            <OccurrenceDateTimeLabel occurrence={occurrence} />
                        </div>
                        <OccurrenceExtras
                            occurrence={occurrence}
                            isChild={isChild}
                            isBlock={isBlock}
                            childOccurrences={[]}
                            open={false}
                            setOpen={() => {}}
                        />
                    </div>
                    <DeliveryIndicator
                        delivery={
                            occurrence.delivery || blockOccurrence?.delivery || course.delivery
                        }
                        locations={[
                            occurrence?.address?.city ||
                                blockOccurrence?.address?.city ||
                                course.address?.city,
                        ]}
                    />
                </div>
                <OccurrencePrice
                    occurrence={occurrence}
                    course={course}
                    hidePricing={hidePricing}
                    isBookable={isBookable}
                    vatSettings={vatSettings}
                />
                <OccurrenceButton
                    isSoldOut={isChild ? isChildSoldOut : isSoldOut}
                    isWaitlistEnabled={isWaitlistEnabled}
                    isBookable={isBookable}
                    isBlock={isBlock}
                    isApplication={isApplication}
                    onClick={handleOccurrenceCardClick}
                />
                {occurrence.description && (
                    <div className={styles.occurrenceDescription}>{occurrence.description}</div>
                )}
            </div>
        </li>
    );
};
